/**
 * Suggestions for HA entity attributes to bind to openHASP object properties.
 *
 * Key format:  "<object_property>:<entity_domain>"
 * Value:       array of { attribute, filter, label }
 *
 *   attribute  — HA state attribute name, or null to use states() (entity state)
 *   filter     — optional Jinja2 filter suffix appended after the value
 *   label      — human-readable description shown in the UI dropdown
 */
export const STATE_ATTRIBUTE_SUGGESTIONS = {
    // ── Slider / arc / gauge / bar / linemeter (val = numeric) ───────────────
    'val:light': [
        { attribute: 'brightness',     filter: '',                   label: 'Brightness (0–255)' },
        { attribute: 'brightness_pct', filter: '| int',              label: 'Brightness % (0–100)' },
        { attribute: 'color_temp',     filter: '',                   label: 'Color temp (mireds)' },
        { attribute: 'color_temp_kelvin', filter: '',                label: 'Color temp (Kelvin)' },
    ],
    'val:cover': [
        { attribute: 'current_position', filter: '',                 label: 'Cover position (0–100)' },
        { attribute: 'current_tilt_position', filter: '',            label: 'Tilt position (0–100)' },
    ],
    'val:climate': [
        { attribute: 'temperature',         filter: '| float * 10 | int', label: 'Set temp ×10 (LVGL int)' },
        { attribute: 'current_temperature', filter: '| float * 10 | int', label: 'Current temp ×10' },
        { attribute: 'humidity',            filter: '| int',              label: 'Humidity %' },
    ],
    'val:fan': [
        { attribute: 'percentage', filter: '| int', label: 'Fan speed % (0–100)' },
    ],
    'val:media_player': [
        { attribute: 'volume_level', filter: '| float * 100 | int', label: 'Volume (0–100)' },
    ],
    'val:humidifier': [
        { attribute: 'humidity', filter: '| int', label: 'Target humidity %' },
    ],
    'val:number': [
        { attribute: null,         filter: '| int', label: 'Number value (from state)' },
    ],
    'val:input_number': [
        { attribute: null,         filter: '| int', label: 'Value (from state)' },
    ],
    'val:sensor': [
        { attribute: null,         filter: '| int', label: 'Sensor value (int)' },
        { attribute: null,         filter: '| float | round(1)', label: 'Sensor value (float)' },
    ],

    // ── Button / switch / checkbox (val = on/off toggle) ─────────────────────
    'val:switch': [
        { attribute: null, filter: '', label: 'State (on=1 / off=0)', toggleMode: true },
    ],
    'val:binary_sensor': [
        { attribute: null, filter: '', label: 'State (on=1 / off=0)', toggleMode: true },
    ],
    'val:input_boolean': [
        { attribute: null, filter: '', label: 'State (on=1 / off=0)', toggleMode: true },
    ],

    // ── Label / text display ──────────────────────────────────────────────────
    'text:sensor': [
        { attribute: null,            filter: '',                      label: 'State value' },
        { attribute: null,            filter: '| float | round(1)',    label: 'State value (1 decimal)' },
        { attribute: 'unit_of_measurement', filter: '',                label: 'Unit of measurement' },
        { attribute: 'friendly_name', filter: '',                      label: 'Friendly name' },
    ],
    'text:weather': [
        { attribute: null,            filter: '',                      label: 'Condition (state)' },
        { attribute: 'temperature',   filter: '',                      label: 'Temperature' },
        { attribute: 'temperature',   filter: '| float | round(1)',    label: 'Temperature (1 decimal)' },
        { attribute: 'humidity',      filter: '',                      label: 'Humidity' },
    ],
    'text:climate': [
        { attribute: 'temperature',         filter: '',                     label: 'Set temperature' },
        { attribute: 'temperature',         filter: '| float | round(1)',   label: 'Set temperature (1 decimal)' },
        { attribute: 'current_temperature', filter: '',                     label: 'Current temperature' },
        { attribute: 'current_temperature', filter: '| float | round(1)',   label: 'Current temperature (1 decimal)' },
        { attribute: 'hvac_mode',           filter: '',                     label: 'HVAC mode' },
    ],
    'text:media_player': [
        { attribute: 'media_title',   filter: '',               label: 'Media title' },
        { attribute: 'media_artist',  filter: '',               label: 'Artist' },
    ],

    // ── Color picker (color = hex rgb) ───────────────────────────────────────
    // rgb_color attribute needs special formatting (list → hex string)
    'color:light': [
        { attribute: 'rgb_color', filter: '', label: 'RGB color → hex', rgbConvert: true },
        { attribute: 'hs_color', filter: '', label: 'Hue / saturation' },
        { attribute: 'xy_color', filter: '', label: 'XY color' },
        { attribute: 'color_temp_kelvin', filter: '', label: 'Color temp (Kelvin)' },
    ],

    // ── Background color (bg_color = hex) ────────────────────────────────────
    'bg_color:light': [
        { attribute: 'rgb_color', filter: '', label: 'Light color → bg hex', rgbConvert: true },
        { attribute: 'hs_color', filter: '', label: 'Hue / saturation' },
        { attribute: 'xy_color', filter: '', label: 'XY color' },
        { attribute: 'color_temp_kelvin', filter: '', label: 'Color temp (Kelvin)' },
    ],
}

/**
 * Return attribute suggestions for a given object property + entity ID.
 * Falls back to an empty array for unknown combinations.
 */
export function getAttributeSuggestions(property, entityId) {
    if (!property || !entityId) return []
    const domain = entityId.split('.')[0]
    return getAttributeSuggestionsForDomains(property, [domain])
}

/**
 * Return the existing property/domain suggestions without requiring a
 * concrete entity. This is the schema capability path used by the HASS
 * builder when a template has no active preview source.
 *
 * @param {string} property
 * @param {string[]} domains
 * @returns {Array<{attribute: string, filter: string, label: string}>}
 */
export function getAttributeSuggestionsForDomains(property, domains) {
    if (!property || !Array.isArray(domains)) return []

    const seen = new Set()
    return domains.flatMap(domain => STATE_ATTRIBUTE_SUGGESTIONS[`${property}:${domain}`] ?? [])
        .filter(suggestion => {
            if (!suggestion.attribute || seen.has(suggestion.attribute)) return false
            seen.add(suggestion.attribute)
            return true
        })
}

/**
 * Resolve known HA attributes from the same V2 widget schema capabilities
 * that drive HASSBuilder's action/domain choices. The optional `domain`
 * narrows the result for a concrete entity; without it, all HA domains the
 * widget can target are considered, which keeps template editing useful
 * without a preview source.
 *
 * `templateEntityBinding` is a schema-declared object capability, not a
 * persisted binding value. It is used only to keep that canonical default
 * available when a schema has no property/domain suggestion row yet.
 *
 * @param {string} property
 * @param {object} schema
 * @param {string} [domain]
 * @returns {Array<{attribute: string, filter: string, label: string}>}
 */
export function getAttributeSuggestionsForSchema(property, schema, domain = '') {
    if (!property || !schema) return []

    const declaredDomains = [
        ...(schema.automation?.actionDomains ?? []),
        ...(schema.automation?.supportedActions ?? []),
        ...(schema.bindings?.actions?.hass_services ?? []),
    ].filter(value => typeof value === 'string' && value)
    const domains = domain ? [domain] : [...new Set(declaredDomains)]
    const suggestions = getAttributeSuggestionsForDomains(property, domains)
    const binding = schema.templateEntityBinding

    if (binding?.property === property && binding.attribute &&
        !suggestions.some(suggestion => suggestion.attribute === binding.attribute)) {
        suggestions.unshift({
            attribute: binding.attribute,
            filter: binding.filter ?? '',
            label: binding.attribute,
        })
    }

    return suggestions
}
